import { useEffect, useRef, useState } from "react";
import FileGlyph from "./FileGlyph";
import { fileTypeOf } from "./fileTypes";
import useStyles from "./style";

/**
 * The 112px preview panel on a file card.
 *
 * Shows a miniature of the file's first page — images straight from the signed
 * URL, PDFs rendered to a canvas — and falls back to a large centred type
 * glyph for everything else, including anything that fails to load. The panel
 * is a fixed height either way, so a card never reflows once its preview
 * resolves.
 *
 * Work starts only once the card scrolls into view, so a folder holding
 * hundreds of documents paints immediately and decodes as you scroll.
 */
const FilePreview = ({ file }) => {
  const { classes, cx } = useStyles();
  const { kind } = fileTypeOf(file);
  const containerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  // Reset when the card is recycled onto a different document.
  useEffect(() => {
    setSrc(null);
    setFailed(false);
  }, [file?.fileUrl]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || visible) return undefined;

    // Older browsers (and jsdom) have no IntersectionObserver — decode straight
    // away rather than showing nothing at all.
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      // Start a screenful early so previews are usually ready by the time the
      // card is actually looked at.
      { rootMargin: "300px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible || kind !== "pdf" || !file?.fileUrl || src || failed) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const { renderPdfThumbnail } = await import("./pdfThumbnail");
        const dataUrl = await renderPdfThumbnail(file.fileUrl);
        if (!cancelled) setSrc(dataUrl);
      } catch (error) {
        // Every reason a PDF can refuse to render lands here — the glyph is a
        // perfectly good stand-in, so this is never surfaced as an error.
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [visible, kind, file?.fileUrl, src, failed]);

  const glyphFallback = (
    <span className={classes.previewGlyph}>
      <FileGlyph file={file} size={44} />
    </span>
  );

  let body = glyphFallback;

  if (!failed && kind === "img" && file?.fileUrl) {
    body = (
      <img
        className={cx(classes.previewSheet, classes.previewPhoto)}
        src={visible ? file.fileUrl : undefined}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  } else if (!failed && kind === "pdf") {
    body = (
      <span className={classes.previewSheet}>
        {src && <img className={classes.previewPage} src={src} alt="" />}
      </span>
    );
  }

  return (
    <div className={classes.previewPanel} ref={containerRef}>
      {body}
    </div>
  );
};

export default FilePreview;
